import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useState as useStateAlias, useEffect as useEffectAlias } from 'react';
import { supabase } from '@/lib/supabase';

const useCountUp = (target: number, duration = 1400, decimals = 0) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  const ref = (el: HTMLElement | null) => {
    if (!el || startedRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || startedRef.current) return;
      startedRef.current = true;
      observer.disconnect();
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        setValue(parseFloat((eased * target).toFixed(decimals)));
        if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        else setValue(target);
      };
      rafRef.current = requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    observer.observe(el);
  };

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);
  return { value, ref };
};

const orderTabs = [
  { label: '待付款', icon: 'ri-time-line', status: '待付款', color: 'text-amber-500' },
  { label: '待发货', icon: 'ri-inbox-archive-line', status: '待发货', color: 'text-blue-500' },
  { label: '待收货', icon: 'ri-truck-line', status: '待收货', color: 'text-emerald-500' },
  { label: '已完成', icon: 'ri-check-double-line', status: '已完成', color: 'text-gray-400' },
];

const serviceItems = [
  { icon: 'ri-wallet-3-line', label: '我的积分', path: '/wallet', gradient: 'from-rose-400 to-rose-500' },
  { icon: 'ri-send-plane-line', label: '转积分', path: '/transfer', gradient: 'from-orange-400 to-amber-400' },
  { icon: 'ri-group-line', label: '我的社区', path: '/community', gradient: 'from-teal-400 to-emerald-500' },
  { icon: 'ri-gift-line', label: '邀请好友', path: '/invite', gradient: 'from-rose-500 to-pink-400' },
  { icon: 'ri-roadster-line', label: '直通车', path: '/expressway', gradient: 'from-indigo-400 to-violet-500' },
  { icon: 'ri-feedback-line', label: '留言反馈', path: '/feedback', gradient: 'from-amber-400 to-orange-300' },
  { icon: 'ri-equalizer-line', label: '系统设置', path: '/settings', gradient: 'from-gray-500 to-gray-400' },
];

const levelList = [
  { name: '游客',     threshold: 0,          direct: '—',   reward: '—',        color: 'from-gray-200 to-gray-100' },
  { name: '普通用户', threshold: 0,          direct: '—',   reward: '—',        color: 'from-gray-300 to-gray-200' },
  { name: '会员',     threshold: 300,        direct: '20%', reward: '60/180',   color: 'from-gray-400 to-gray-300' },
  { name: '销售员',   threshold: 9000,       direct: '25%', reward: '75/225',   color: 'from-emerald-400 to-teal-300' },
  { name: '批发',     threshold: 45000,      direct: '30%', reward: '90/270',   color: 'from-sky-400 to-cyan-300' },
  { name: '主管',     threshold: 270000,     direct: '35%', reward: '105/315',  color: 'from-amber-400 to-yellow-300' },
  { name: '经理',     threshold: 1350000,    direct: '40%', reward: '120/360',  color: 'from-orange-400 to-amber-300' },
  { name: '总监',     threshold: 4500000,    direct: '45%', reward: '135/405',  color: 'from-rose-400 to-pink-300' },
  { name: '董事',     threshold: 32400000,   direct: '50%', reward: '150/450',  color: 'from-violet-400 to-purple-300' },
  { name: '总裁',     threshold: 90000000,   direct: '55%', reward: '165/495',  color: 'from-red-500 to-rose-400' },
];

const getLevelInfo = (levelName: string, points = 0) => {
  const idx = levelList.findIndex((l) => l.name === levelName);
  const current = levelList[idx] ?? levelList[0];
  const next = levelList[idx + 1] ?? null;
  const progress = next
    ? Math.min(
        Math.max(
          Math.round(((points - current.threshold) / (next.threshold - current.threshold)) * 100),
          0
        ),
        99
      )
    : 100;
  return { current, next, progress };
};

const medalConfig: Record<string, { outer: string; inner: string; star: string; shine: string; label: string }> = {
  '游客':     { outer: '#e2e6ea', inner: '#f0f3f5', star: '#c0ccd6', shine: '#f8f9fa', label: '#a0aab4' },
  '普通用户': { outer: '#d0d5db', inner: '#e8ecef', star: '#a8b4c0', shine: '#f5f7f9', label: '#8a9aa8' },
  '会员':     { outer: '#b0b8c1', inner: '#d4dbe2', star: '#8a9bb0', shine: '#e8edf2', label: '#7a8a9a' },
  '销售员':   { outer: '#34c98a', inner: '#5ddba8', star: '#1fa870', shine: '#a8f0d4', label: '#1a9060' },
  '批发':     { outer: '#38b6e8', inner: '#60ccf5', star: '#1a9fd4', shine: '#b0e8fc', label: '#1580b0' },
  '主管':     { outer: '#f5a623', inner: '#ffc84a', star: '#d4880a', shine: '#ffe8a0', label: '#b87010' },
  '经理':     { outer: '#f07830', inner: '#f5a060', star: '#d05010', shine: '#fdd0a8', label: '#b04010' },
  '总监':     { outer: '#e8405a', inner: '#f07080', star: '#c01838', shine: '#fbb0c0', label: '#a01030' },
  '董事':     { outer: '#9b59d0', inner: '#c080f0', star: '#7030b0', shine: '#e0b8fc', label: '#6020a0' },
  '总裁':     { outer: '#e8302a', inner: '#ff6060', star: '#c01010', shine: '#ffb0b0', label: '#a00808' },
};

const levelIconType: Record<string, 'person' | 'star' | 'shield' | 'diamond' | 'trophy' | 'crown' | 'royal' | 'gem' | 'supreme'> = {
  '游客':     'person',
  '普通用户': 'person',
  '会员': 'star',
  '销售员': 'shield',
  '批发': 'diamond',
  '主管': 'trophy',
  '经理': 'crown',
  '总监': 'royal',
  '董事': 'gem',
  '总裁': 'supreme',
};

const makePersonPath = (cx: number, cy: number, r: number) => {
  const headR = r * 0.35;
  const bodyW = r * 0.6;
  const bodyH = r * 0.7;
  const headY = cy - r * 0.35;
  const bodyY = cy + r * 0.1;
  const head = `M${cx},${headY - headR} A${headR},${headR} 0 1,1 ${cx},${headY + headR} A${headR},${headR} 0 1,1 ${cx},${headY - headR} Z`;
  const body = `M${cx - bodyW * 0.4},${bodyY} L${cx + bodyW * 0.4},${bodyY} L${cx + bodyW * 0.7},${bodyY + bodyH} L${cx - bodyW * 0.7},${bodyY + bodyH} Z`;
  return `${head} ${body}`;
};

const makeStarPath = (cx: number, cy: number, outerR: number, innerR: number, points: number) => {
  return Array.from({ length: points * 2 }, (_, i) => {
    const angle = (Math.PI / points) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    return `${i === 0 ? 'M' : 'L'}${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  }).join(' ') + ' Z';
};

const makeShieldPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.9;
  const h = r * 1.1;
  const topY = cy - h * 0.7;
  const bottomY = cy + h * 0.5;
  return `M${cx - w},${topY} Q${cx - w},${cy} ${cx},${bottomY} Q${cx + w},${cy} ${cx + w},${topY} Q${cx + w},${cy - h * 0.3} ${cx},${cy - h * 0.5} Q${cx - w},${cy - h * 0.3} ${cx - w},${topY} Z`;
};

const makeDiamondPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.8;
  const h = r * 1.0;
  return `M${cx},${cy - h} L${cx + w},${cy} L${cx},${cy + h * 0.6} L${cx - w},${cy} Z`;
};

const makeTrophyPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.7;
  const h = r * 0.9;
  const cup = `M${cx - w},${cy - h * 0.3} Q${cx - w},${cy + h * 0.4} ${cx},${cy + h * 0.5} Q${cx + w},${cy + h * 0.4} ${cx + w},${cy - h * 0.3} L${cx + w * 0.7},${cy - h * 0.3} Q${cx + w * 0.7},${cy + h * 0.2} ${cx},${cy + h * 0.3} Q${cx - w * 0.7},${cy + h * 0.2} ${cx - w * 0.7},${cy - h * 0.3} Z`;
  const base = `M${cx - w * 0.4},${cy + h * 0.5} L${cx + w * 0.4},${cy + h * 0.5} L${cx + w * 0.5},${cy + h * 0.7} L${cx - w * 0.5},${cy + h * 0.7} Z`;
  const leftHandle = `M${cx - w},${cy - h * 0.1} Q${cx - w * 1.4},${cy} ${cx - w},${cy + h * 0.2}`;
  const rightHandle = `M${cx + w},${cy - h * 0.1} Q${cx + w * 1.4},${cy} ${cx + w},${cy + h * 0.2}`;
  return `${cup} ${base} ${leftHandle} ${rightHandle}`;
};

const makeCrownPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.85;
  const baseY = cy + r * 0.3;
  const topY = cy - r * 0.5;
  const midY = cy - r * 0.1;
  return `M${cx - w},${baseY} L${cx - w * 0.6},${midY} L${cx - w * 0.3},${topY} L${cx},${midY} L${cx + w * 0.3},${topY} L${cx + w * 0.6},${midY} L${cx + w},${baseY} L${cx + w * 0.8},${baseY} L${cx + w * 0.6},${cy + r * 0.1} L${cx - w * 0.6},${cy + r * 0.1} L${cx - w * 0.8},${baseY} Z`;
};

const makeRoyalCrownPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.9;
  const baseY = cy + r * 0.35;
  const topY = cy - r * 0.55;
  const midY = cy - r * 0.15;
  const body = `M${cx - w},${baseY} L${cx - w * 0.7},${midY} L${cx - w * 0.35},${topY} L${cx},${midY} L${cx + w * 0.35},${topY} L${cx + w * 0.7},${midY} L${cx + w},${baseY} Z`;
  const base = `M${cx - w * 1.05},${baseY} L${cx + w * 1.05},${baseY} L${cx + w * 1.1},${baseY + r * 0.15} L${cx - w * 1.1},${baseY + r * 0.15} Z`;
  const gem = `M${cx},${topY - r * 0.15} L${cx + r * 0.12},${topY + r * 0.05} L${cx},${topY + r * 0.15} L${cx - r * 0.12},${topY + r * 0.05} Z`;
  return `${body} ${base} ${gem}`;
};

const makeGemPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.75;
  const h = r * 0.9;
  return `M${cx},${cy - h} L${cx + w * 0.87},${cy - h * 0.5} L${cx + w * 0.87},${cy + h * 0.5} L${cx},${cy + h} L${cx - w * 0.87},${cy + h * 0.5} L${cx - w * 0.87},${cy - h * 0.5} Z`;
};

const makeSupremeCrownPath = (cx: number, cy: number, r: number) => {
  const w = r * 0.95;
  const baseY = cy + r * 0.4;
  const topY = cy - r * 0.6;
  const midY = cy - r * 0.1;
  const body = `M${cx - w},${baseY} L${cx - w * 0.75},${midY} L${cx - w * 0.5},${topY + r * 0.2} L${cx - w * 0.25},${midY} L${cx},${topY} L${cx + w * 0.25},${midY} L${cx + w * 0.5},${topY + r * 0.2} L${cx + w * 0.75},${midY} L${cx + w},${baseY} Z`;
  const base = `M${cx - w * 1.1},${baseY} L${cx + w * 1.1},${baseY} L${cx + w * 1.15},${baseY + r * 0.18} L${cx - w * 1.15},${baseY + r * 0.18} Z`;
  const centerGem = `M${cx},${topY - r * 0.1} L${cx + r * 0.15},${topY + r * 0.15} L${cx},${topY + r * 0.35} L${cx - r * 0.15},${topY + r * 0.15} Z`;
  const leftStar = makeStarPath(cx - w * 0.85, cy - r * 0.1, r * 0.15, r * 0.07, 5);
  const rightStar = makeStarPath(cx + w * 0.85, cy - r * 0.1, r * 0.15, r * 0.07, 5);
  return `${body} ${base} ${centerGem} ${leftStar} ${rightStar}`;
};

const LevelMedal = ({ levelName, size = 36 }: { levelName: string; size?: number }) => {
  const cfg = medalConfig[levelName] ?? medalConfig['会员'];
  const iconType = levelIconType[levelName] ?? 'star';
  const cx = size / 2;
  const cy = size / 2;
  const uid = levelName;
  const outerR = size * 0.47;
  const innerR = size * 0.38;
  const circleR = size * 0.32;
  const iconR = size * 0.22;

  const makePolyPoints = (r1: number, r2: number, n: number) =>
    Array.from({ length: n * 2 }, (_, i) => {
      const angle = (Math.PI / n) * i - Math.PI / 2;
      const r = i % 2 === 0 ? r1 : r2;
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    }).join(' ');

  const outerDecoration = (() => {
    switch (iconType) {
      case 'person':
        return (
          <>
            <circle cx={cx} cy={cy} r={outerR} fill={cfg.outer} />
            <circle cx={cx} cy={cy} r={outerR * 0.8} fill="none" stroke={cfg.shine} strokeWidth="1.5" />
          </>
        );
      case 'star':
        return <polygon points={makePolyPoints(outerR, innerR, 10)} fill={cfg.outer} />;
      case 'shield':
      case 'diamond':
        return (
          <>
            <circle cx={cx} cy={cy} r={outerR} fill={cfg.outer} />
            <circle cx={cx} cy={cy} r={outerR * 0.85} fill="none" stroke={cfg.shine} strokeWidth="1" strokeDasharray="3 2" />
          </>
        );
      case 'trophy':
      case 'crown':
        return (
          <>
            <polygon points={makePolyPoints(outerR, outerR * 0.88, 12)} fill={cfg.outer} />
            <circle cx={cx} cy={cy} r={innerR} fill="none" stroke={cfg.shine} strokeWidth="1.5" />
          </>
        );
      case 'royal':
      case 'gem':
      case 'supreme':
        return (
          <>
            <polygon points={makePolyPoints(outerR, outerR * 0.9, 16)} fill={cfg.outer} />
            <polygon points={makePolyPoints(innerR * 1.05, innerR * 0.92, 8)} fill="none" stroke={cfg.shine} strokeWidth="1" />
          </>
        );
      default:
        return <circle cx={cx} cy={cy} r={outerR} fill={cfg.outer} />;
    }
  })();

  const iconPath = (() => {
    switch (iconType) {
      case 'person': return makePersonPath(cx, cy, iconR);
      case 'star': return makeStarPath(cx, cy, iconR, iconR * 0.4, 5);
      case 'shield': return makeShieldPath(cx, cy, iconR);
      case 'diamond': return makeDiamondPath(cx, cy, iconR);
      case 'trophy': return makeTrophyPath(cx, cy, iconR);
      case 'crown': return makeCrownPath(cx, cy, iconR);
      case 'royal': return makeRoyalCrownPath(cx, cy, iconR);
      case 'gem': return makeGemPath(cx, cy, iconR);
      case 'supreme': return makeSupremeCrownPath(cx, cy, iconR);
      default: return makeStarPath(cx, cy, iconR, iconR * 0.4, 5);
    }
  })();

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))', flexShrink: 0 }}>
      <defs>
        <radialGradient id={`grad-${uid}`} cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor={cfg.shine} />
          <stop offset="100%" stopColor={cfg.inner} />
        </radialGradient>
        <filter id={`glow-${uid}`}>
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {outerDecoration}
      <circle cx={cx} cy={cy} r={circleR} fill={`url(#grad-${uid})`} />
      <path d={iconPath} fill={cfg.star} filter={`url(#glow-${uid})`} />
    </svg>
  );
};

const levelBadgeColors: Record<string, string> = {
  '游客':     'bg-gray-50 text-gray-300',
  '普通用户': 'bg-gray-100 text-gray-400',
  '会员': 'bg-gray-100 text-gray-500',
  '销售员': 'bg-emerald-50 text-emerald-600',
  '批发': 'bg-sky-50 text-sky-600',
  '主管': 'bg-amber-50 text-amber-600',
  '经理': 'bg-orange-50 text-orange-600',
  '总监': 'bg-rose-50 text-rose-600',
  '董事': 'bg-violet-50 text-violet-600',
  '总裁': 'bg-red-50 text-red-600',
};

const LevelModal = ({ onClose, currentLevelName }: { onClose: () => void; currentLevelName: string }) => (
  <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40" />
    <div
      className="relative w-full max-w-[480px] bg-white rounded-t-3xl overflow-hidden"
      style={{ animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-10 h-1 bg-gray-200 rounded-full" />
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
        <h2 className="font-bold text-gray-900 text-base">推广等级制度</h2>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400">
          <i className="ri-close-line text-xl" />
        </button>
      </div>
      <div className="px-5 py-3 bg-rose-50/60">
        <p className="text-xs text-gray-500 leading-relaxed">
          <span className="text-gray-700 font-medium">晋升规则：</span>累计消费积分达到对应阈值即可自动晋升。<br />
          <span className="text-gray-700 font-medium">收益规则：</span>直推返佣按等级比例发放；平级差为直推返佣的 <span className="text-rose-500 font-medium">10%</span>；团队级差收益：所有上级均可获得下级消费的 <span className="text-rose-500 font-medium">5%</span>，无限叠加。<br />
          <span className="text-gray-400">注：会员等级不享受平级差收益。</span>
        </p>
      </div>
      <div className="overflow-y-auto max-h-[60vh] px-4 py-3 space-y-2.5 pb-8">
        {levelList.map((lv, idx) => {
          const isCurrent = lv.name === currentLevelName;
          return (
            <div
              key={lv.name}
              className={`rounded-2xl p-4 border transition-all ${isCurrent ? 'border-rose-300 bg-rose-50/60' : 'border-gray-100 bg-gray-50/60'}`}
            >
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs font-medium w-4">{idx + 1}</span>
                  <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${levelBadgeColors[lv.name] ?? 'bg-gray-100 text-gray-500'}`}>
                    {lv.name}
                  </span>
                  {isCurrent && (
                    <span className="text-xs bg-rose-500 text-white px-2 py-0.5 rounded-full font-medium">当前</span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  {lv.threshold === 0 ? '免费注册' : `消费 ${lv.threshold.toLocaleString()} 积分`}
                </span>
              </div>
              {lv.name === '游客' ? (
                <div className="bg-white rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-information-line text-gray-400 text-base flex-shrink-0" />
                  <p className="text-xs text-gray-500 leading-relaxed">
                    未注册状态，注册后自动成为 <span className="text-gray-700 font-medium">普通用户</span>
                  </p>
                </div>
              ) : lv.name === '普通用户' ? (
                <div className="bg-white rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-information-line text-amber-400 text-base flex-shrink-0" />
                  <p className="text-xs text-gray-500 leading-relaxed">
                    免费注册即可，累计消费积分达到 <span className="text-rose-500 font-medium">300</span> 积分后自动晋升为会员，解锁分销权益
                  </p>
                </div>
              ) : lv.name === '会员' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <p className="text-rose-500 font-bold text-sm">{lv.direct}</p>
                    <p className="text-xs text-gray-400 mt-0.5">直推返佣</p>
                  </div>
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <p className="text-gray-800 font-bold text-sm">{lv.reward}</p>
                    <p className="text-xs text-gray-400 mt-0.5">销售奖励</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <p className="text-rose-500 font-bold text-sm">{lv.direct}</p>
                    <p className="text-xs text-gray-400 mt-0.5">直推返佣</p>
                  </div>
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <p className="text-gray-800 font-bold text-sm">{lv.reward}</p>
                    <p className="text-xs text-gray-400 mt-0.5">销售奖励</p>
                  </div>
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <p className="text-emerald-500 font-bold text-sm">5%</p>
                    <p className="text-xs text-gray-400 mt-0.5">团队级差</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const MinePage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const { user, loading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  const levelName = profile?.level ?? '会员';
  const userPerformance = profile?.performance ?? 0;
  const { current: level, next: nextLevel, progress: levelProgress } = getLevelInfo(levelName, userPerformance);

  const pointsTarget = profile?.points ?? 0;
  const balanceTarget = profile?.balance ?? 0;

  const [teamCount, setTeamCount] = useStateAlias(0);
  useEffectAlias(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('custom_auth_token');

    const fetchViaEdge = async () => {
      if (!token) throw new Error('no token');
      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/custom-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY },
        body: JSON.stringify({ action: 'get_team_count', userId: user.id, userToken: token }),
      });
      const data = await res.json();
      if (data.success && typeof data.count === 'number') {
        setTeamCount(data.count);
        return true;
      }
      throw new Error(data.error ?? 'failed');
    };

    fetchViaEdge().catch(() => {
      supabase
        .rpc('get_team_count_recursive', { root_user_id: user.id })
        .then(({ data, error }) => {
          if (!error && typeof data === 'number') {
            setTeamCount(data);
          } else {
            supabase
              .from('custom_users')
              .select('id', { count: 'exact', head: true })
              .eq('referrer_id', user.id)
              .then(({ count }) => { if (count != null) setTeamCount(count); });
          }
        });
    });
  }, [user?.id]);

  const points = useCountUp(pointsTarget, 1400, 3);
  const balance = useCountUp(balanceTarget, 800, 3);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [dbOrders, setDbOrders] = useStateAlias<Array<{ id: string; status: string; items: Array<{ name: string; image?: string; spec?: string }> | null; total_amount: number; created_at: string }>>([]);
  useEffectAlias(() => {
    if (!user) return;
    supabase.from('orders').select('id, status, items, total_amount, created_at').eq('user_id', user.id).order('created_at', { ascending: false }).then(({ data }) => {
      if (data) setDbOrders(data as typeof dbOrders);
    });
  }, [user]);
  const getOrderCount = (status: string) => dbOrders.filter((o) => o.status === status).length;

  const [todayIncome, setTodayIncome] = useStateAlias(0);
  useEffectAlias(() => {
    if (!user) return;
    const now = new Date();
    const cstMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const utcMidnight = new Date(cstMidnight.getTime() - 8 * 60 * 60 * 1000);
    supabase
      .from('points_log')
      .select('delta')
      .eq('user_id', user.id)
      .gt('delta', 0)
      .neq('source', 'refund')
      .gte('created_at', utcMidnight.toISOString())
      .then(({ data }) => {
        if (data) {
          const total = data.reduce((sum, r) => sum + (r.delta ?? 0), 0);
          setTodayIncome(total);
        }
      });
  }, [user]);

  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#f7f7f9] pb-24 max-w-[480px] mx-auto relative">
        <div className={`fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
          <div className="flex items-center justify-between px-4 pt-10 pb-3">
            <span className={`font-bold text-base transition-opacity duration-300 ${scrolled ? 'opacity-100 text-gray-900' : 'opacity-0'}`}>我的</span>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/settings')} className="flex flex-col items-center gap-0.5 cursor-pointer">
                <i className={`ri-settings-3-line text-xl transition-colors duration-300 ${scrolled ? 'text-gray-700' : 'text-white'}`} />
                <span className={`text-xs transition-colors duration-300 ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>设置</span>
              </button>
            </div>
          </div>
        </div>
        <div className="relative h-52 bg-gradient-to-br from-rose-700 via-rose-600 to-orange-500 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10" />
          <div className="absolute top-16 -right-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-white/10" />
          <div className="absolute bottom-8 left-0 right-0 px-5 flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-white text-3xl" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-lg mb-1">未登录</p>
              <button
                onClick={() => navigate('/login', { state: { from: '/mine' } })}
                className="bg-white text-rose-500 text-sm font-semibold px-5 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                登录 / 注册
              </button>
            </div>
          </div>
        </div>
        <div className="mx-4 -mt-4 relative z-10">
          <div className="bg-white rounded-2xl p-5 text-center">
            <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-lock-line text-rose-400 text-2xl" />
            </div>
            <p className="text-gray-800 font-bold text-base mb-1.5">登录后解锁全部功能</p>
            <p className="text-gray-400 text-sm mb-4">查看订单、积分、钱包、分销收益等</p>
            <button
              onClick={() => navigate('/login', { state: { from: '/mine' } })}
              className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-3.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap"
            >
              立即登录
            </button>
          </div>
        </div>
        <div className="bg-white mt-3 mx-4 rounded-2xl p-4">
          <h3 className="font-bold text-gray-900 text-sm mb-4">我的服务</h3>
          <div className="grid grid-cols-3 gap-3">
            {serviceItems.map((item) => (
              <button
                key={item.label}
                onClick={() => navigate('/login', { state: { from: '/mine' } })}
                className="flex flex-col items-center gap-2 py-2 rounded-xl cursor-pointer active:bg-gray-50 transition-colors opacity-50"
              >
                <div className={`w-[52px] h-[52px] flex items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient}`}>
                  <i className={`${item.icon} text-[22px] text-white`} />
                </div>
                <span className="text-xs text-gray-500 font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-[#f7f7f9] flex items-center justify-center max-w-[480px] mx-auto">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const displayName = profile?.nickname ?? (user as any)?.username ?? '用户';
  const displayPhone = profile?.phone || '';
  const displayAvatar = profile?.avatar_url || '';

  const avatarColors = [
    'from-rose-400 to-pink-500',
    'from-orange-400 to-amber-500',
    'from-emerald-400 to-teal-500',
    'from-violet-400 to-purple-500',
    'from-sky-400 to-cyan-500',
    'from-red-400 to-rose-500',
    'from-indigo-400 to-blue-500',
    'from-fuchsia-400 to-pink-500',
  ];
  const avatarChar = displayName.charAt(0) || '用';
  const colorIndex = displayName.split('').reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % avatarColors.length;
  const avatarGradient = avatarColors[colorIndex];

  return (
    <div className="min-h-screen bg-[#f7f7f9] pb-24 max-w-[480px] mx-auto relative">
      <div className={`fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="flex items-center justify-between px-4 pt-10 pb-3">
          <span className={`font-bold text-base transition-opacity duration-300 ${scrolled ? 'opacity-100 text-gray-900' : 'opacity-0'}`}>我的</span>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/contact')} className="flex flex-col items-center gap-0.5 cursor-pointer">
              <i className={`ri-customer-service-2-line text-xl transition-colors duration-300 ${scrolled ? 'text-gray-700' : 'text-white'}`} />
              <span className={`text-xs transition-colors duration-300 ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>客服</span>
            </button>
            <button onClick={() => navigate('/settings')} className="flex flex-col items-center gap-0.5 cursor-pointer">
              <i className={`ri-settings-3-line text-xl transition-colors duration-300 ${scrolled ? 'text-gray-700' : 'text-white'}`} />
              <span className={`text-xs transition-colors duration-300 ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>设置</span>
            </button>
          </div>
        </div>
      </div>

      <div className="relative h-36 overflow-hidden" style={{ background: 'linear-gradient(90deg, #7a8499 0%, #8D97AA 100%)' }}>
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute top-16 -right-6 w-28 h-28 rounded-full bg-white/10" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-white/10" />
        <div className="absolute bottom-12 left-0 right-0 px-5 flex items-end gap-4">
          <div className="relative flex-shrink-0">
            <div className="rounded-2xl overflow-hidden border-2 border-white/60 shadow-lg" style={{ width: 64, height: 64 }}>
              {displayAvatar ? (
                <img src={displayAvatar} alt="avatar" className="w-full h-full object-cover object-top" />
              ) : (
                <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarGradient}`}>
                  <span className="text-white font-bold text-2xl leading-none">{avatarChar}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 pb-1">
            <p className="text-white font-bold text-xl leading-none mb-1.5">{displayName}</p>
            <p className="text-white/70 text-sm mb-2">{displayPhone}</p>
            <div className="flex items-center gap-1.5">
              <LevelMedal levelName={level.name} size={28} />
              <span className="text-white font-semibold text-sm">{level.name}</span>
              <span className="text-white/60 text-xs">· 直推 {level.direct}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 pt-4 pb-3 border-b border-gray-50 cursor-pointer active:bg-gray-50 transition-colors" onClick={() => setShowLevelModal(true)}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-1.5">
                <i className="ri-vip-crown-line text-amber-400 text-sm" />
                {level.name === '普通用户' ? (
                  <span className="text-xs text-gray-500">
                    当前 <span className="text-gray-800 font-medium">普通用户</span>
                    &nbsp;· 消费 <span className="text-rose-500 font-medium">300</span> 积分升级会员，还差 <span className="text-amber-500 font-medium">{Math.max(0, 300 - userPerformance)}</span> 积分
                  </span>
                ) : nextLevel ? (
                  <span className="text-xs text-gray-500">
                    当前 <span className="text-gray-800 font-medium">{level.name}</span>
                    &nbsp;· 直推返佣 <span className="text-rose-500 font-medium">{level.direct}</span>
                    &nbsp;· 升级至 <span className="text-amber-500 font-medium">{nextLevel.name}</span>
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">
                    已达最高等级 <span className="text-rose-500 font-medium">{level.name}</span>
                    &nbsp;· 直推返佣 <span className="text-rose-500 font-medium">{level.direct}</span>
                  </span>
                )}
              </div>
              {nextLevel && levelProgress > 0 && (
                <span className="text-xs text-gray-400">{levelProgress}%</span>
              )}
              <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${level.color} transition-all duration-1000`}
                style={{ width: `${Math.max(levelProgress, levelProgress > 0 ? levelProgress : 2)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <div className="flex items-center gap-2">
                {level.name !== '普通用户' && (
                  <>
                    <span className="text-xs text-gray-400">销售奖励 <span className="text-gray-600 font-medium">{level.reward}</span></span>
                    {level.name !== '会员' && (
                      <>
                        <span className="text-gray-200">|</span>
                        <span className="text-xs text-gray-400">团队级差 <span className="text-gray-600 font-medium">5%</span></span>
                      </>
                    )}
                  </>
                )}
              </div>
              {nextLevel ? (
                <span className="text-xs text-rose-400 font-medium whitespace-nowrap">
                  还差 {Math.max(0, nextLevel.threshold - userPerformance).toLocaleString()} 积分
                </span>
              ) : (
                <span className="text-xs text-amber-500 font-medium whitespace-nowrap">已达最高等级</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-gray-50">
            <button onClick={() => navigate('/wallet')} className="flex flex-col items-center py-4 gap-0.5 cursor-pointer active:bg-gray-50 transition-colors">
              <p
                ref={points.ref as React.RefCallback<HTMLParagraphElement>}
                className="text-rose-500 font-bold text-xl tabular-nums leading-none"
              >
                {points.value.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
              </p>
              <p className="text-xs text-gray-400 mt-1">积分余额</p>
            </button>
            <button onClick={() => navigate('/community')} className="flex flex-col items-center py-4 gap-0.5 cursor-pointer active:bg-gray-50 transition-colors">
              <p className="text-gray-800 font-bold text-xl tabular-nums leading-none">
                {teamCount}
              </p>
              <p className="text-xs text-gray-400 mt-1">团队人数</p>
            </button>
          </div>
        </div>
      </div>

      {level.name === '普通用户' && (
        <div className="mx-4 mt-3">
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer active:opacity-90 transition-opacity"
            onClick={() => navigate('/shop')}
            style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}
          >
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #ff6b6b, transparent)' }} />
            <div className="relative z-10 px-4 py-4 flex items-center gap-4">
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #ffd700, #ffaa00)' }}
              >
                <i className="ri-vip-crown-2-fill text-white text-2xl" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-white font-bold text-base">升级为会员</span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: 'linear-gradient(90deg, #ffd700, #ffaa00)', color: '#7a4f00' }}
                  >
                    仅差 {Math.max(0, 300 - userPerformance)} 积分
                  </span>
                </div>
                <p className="text-white/60 text-xs leading-relaxed">累计消费 <span className="text-yellow-300 font-semibold">300</span> 积分，自动晋升会员，解锁直推返佣 <span className="text-yellow-300 font-semibold">20%</span> 等全部分销权益</p>
                <div className="flex items-center gap-3 mt-2">
                  {['直推返佣', '销售奖励', '级差收益'].map((tag) => (
                    <span key={tag} className="text-xs text-white/50 flex items-center gap-0.5">
                      <i className="ri-check-line text-yellow-400 text-xs" />{tag}
                    </span>
                  ))}
                </div>
              </div>
              <div
                className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                style={{ background: 'rgba(255,215,0,0.2)' }}
              >
                <i className="ri-arrow-right-s-line text-yellow-400 text-lg" />
              </div>
            </div>
            <div className="relative z-10 px-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/40 text-xs">晋升进度</span>
                <span className="text-white/40 text-xs">累计消费 {userPerformance.toLocaleString()} / 300</span>
              </div>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${Math.min(Math.round((userPerformance / 300) * 100), 100)}%`,
                    background: 'linear-gradient(90deg, #ffd700, #ffaa00)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-4 mt-3">
        <div className="rounded-2xl px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(90deg, #e8d98a 0%, #F2E6A5 100%)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black/10">
              <i className="ri-coins-line text-amber-800 text-base" />
            </div>
            <div>
              <p className="text-amber-800/70 text-xs">今日收益</p>
              <p className="text-amber-900 font-bold text-base leading-none mt-0.5">+{Number(todayIncome).toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} 积分</p>
            </div>
          </div>
          <button onClick={() => navigate('/wallet')} className="flex items-center gap-1 bg-black/10 text-amber-900 text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap">
            明细 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div className="bg-white mt-3 mx-4 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-sm">我的订单</h3>
          <button onClick={() => navigate('/orders')} className="text-rose-500 text-xs flex items-center gap-0.5 cursor-pointer whitespace-nowrap">
            全部 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {orderTabs.map((tab) => {
            const count = getOrderCount(tab.status);
            return (
              <button
                key={tab.status}
                onClick={() => navigate('/orders')}
                className="flex flex-col items-center gap-2 py-2 rounded-xl cursor-pointer active:bg-gray-50 transition-colors"
              >
                <div className="relative w-11 h-11 flex items-center justify-center rounded-2xl bg-gray-50">
                  <i className={`${tab.icon} text-xl ${tab.color}`} />
                  {count > 0 && tab.status !== '已完成' && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-medium">
                      {count}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white mt-3 mx-4 rounded-2xl p-4">
        <h3 className="font-bold text-gray-900 text-sm mb-4">我的服务</h3>
        <div className="grid grid-cols-3 gap-3">
          {serviceItems.map((item) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center gap-2 py-2 rounded-xl cursor-pointer active:scale-95 transition-transform"
            >
              <div className={`w-13 h-13 w-[52px] h-[52px] flex items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient}`}>
                <i className={`${item.icon} text-[22px] text-white`} />
              </div>
              <span className="text-xs text-gray-500 font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white mt-3 mx-4 rounded-2xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-sm">最近兑换</h3>
          <button onClick={() => navigate('/orders')} className="text-rose-500 text-xs flex items-center gap-0.5 cursor-pointer whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
        <div className="space-y-3">
          {dbOrders.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">暂无订单记录</p>
          ) : dbOrders.slice(0, 3).map((order) => {
            const firstItem = Array.isArray(order.items) ? order.items[0] : null;
            return (
              <div
                key={order.id}
                onClick={() => navigate('/orders')}
                className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50/80 cursor-pointer active:bg-gray-100 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-white flex-shrink-0 shadow-sm">
                  {firstItem?.image ? (
                    <img src={firstItem.image} alt={firstItem.name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <i className="ri-shopping-bag-line text-gray-300 text-xl" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-medium line-clamp-1">{firstItem?.name ?? '商品'}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{new Date(order.created_at).toLocaleDateString('zh-CN')}</p>
                  <p className="text-rose-500 text-xs font-semibold mt-1">{Number(order.total_amount).toLocaleString()} 积分</p>
                </div>
                <div className="flex-shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    order.status === '已完成' ? 'bg-gray-100 text-gray-500' :
                    order.status === '待发货' ? 'bg-amber-50 text-amber-500' :
                    order.status === '待收货' ? 'bg-emerald-50 text-emerald-500' :
                    'bg-rose-50 text-rose-500'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showLevelModal && (
        <LevelModal onClose={() => setShowLevelModal(false)} currentLevelName={level.name} />
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MinePage;
